import classes from './LineChart.module.scss';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { ChartHorizontalAxisTick } from './ChartHorizontalAxisTick';
import { LineChartTooltip } from './LineChartTooltip';
import { ChartLegendSocialProfiles } from './ChartLegendSocialProfiles';
import React, { useContext, useState } from 'react';
import { AnalyticsContext, SocialProfilesAnalytics } from '../AnalyticsProvider';
import { FillingDataConfig, ChartLine } from './LineChart.controller';
import { ChartVerticalAxisTick } from './ChartVerticalAxisTick';
import { ChartPanel } from '../ChartPanel';
import { CHART_STROKE_COLOR, FILLING_DATA_FILL_STROKE_COLOR } from '../chartConsts';
import { ConfigChartTicks, TickChart } from '../helpers/chartHelpers';
import classNames from 'classnames';
import { useBreakpoints } from 'hooks/useBreakpoints';

export const STRING_DASHED_PROFILE = 'dashedLineProfile-';
export const STRING_FAKE_VALUE_DASHED = 'fakeValueDashedLine';

type LineChartViewProps = {
  isEngagementChart: boolean;
  data: ChartLine[];
  socialProfiles: SocialProfilesAnalytics[];
  fillingDataConfig: FillingDataConfig;
  chartDomainAxisX: number[];
  chartDomainAxisY: number[];
  loading: boolean;
  hideGridLinesEveryFourLines: boolean;
  hideGridLinesEverySevenLines: boolean;
  isEmpty?: boolean;
  ticksConfig?: ConfigChartTicks;
  chartTitle: string;
  chartSubTitle: string;
  subTitleTooltipText: string;
  ticksChart: TickChart[];
  shouldNormalize: boolean;
  ticksAxisY: number[];
};

enum COLOR_SOCIAL_PROFILES {
  'TwitterSocialProfile' = '#2c2c2c',
  'InstagramSocialProfile' = '#c32aa3',
  'FacebookSocialProfile' = '#1877f2',
  'LinkedInSocialProfile' = '#007bb5',
  'PinterestSocialProfile' = '#bd081c',
  'TikTokSocialProfile' = '',
  'GenericSocialProfile' = '',
  'GoogleSocialProfile' = '',
  'SocialProfile' = ''
}

export const LineChartView = ({
  data,
  socialProfiles,
  ticksChart,
  hideGridLinesEverySevenLines,
  hideGridLinesEveryFourLines,
  fillingDataConfig,
  chartDomainAxisX,
  chartDomainAxisY,
  loading,
  isEmpty,
  ticksConfig,
  chartTitle,
  chartSubTitle,
  subTitleTooltipText,
  shouldNormalize,
  isEngagementChart,
  ticksAxisY
}: LineChartViewProps) => {
  const breakpoint = useBreakpoints();
  const isTablet = breakpoint === 'tabletportrait';
  const { view } = useContext(AnalyticsContext);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const {
    hasFillingData,
    hasPreFillingData,
    hasPostFillingData,
    referenceAreaPreFillingCoordinates,
    referenceAreaPostFillingCoordinates
  } = fillingDataConfig;
  const onMouseEnter = (id: string) => {
    setActiveProfileId(id);
  };
  const onMouseLeave = () => {
    setActiveProfileId(null);
  };
  return (
    <ChartPanel
      loading={loading}
      chartTitle={chartTitle}
      chartSubtitle={chartSubTitle}
      subTitleTooltipText={subTitleTooltipText}
      isEmpty={isEmpty}>
      {!loading && !isEmpty && (
        <ResponsiveContainer
          height={485}
          className={classNames({
            [classes.HideGridLinesEveryTwoLines]: ticksConfig?.showLabelAfterEveryNTicks === 2,
            [classes.HideGridLinesEveryFourLines]: hideGridLinesEveryFourLines && !hasPreFillingData,
            [classes.HideGridLinesEverySevenLines]: hideGridLinesEverySevenLines,
            [classes.HideGridLinesEveryFourLinesWithPreFill]: hideGridLinesEveryFourLines && hasPreFillingData,
            [classes.NotHideLastLine]:
              hideGridLinesEveryFourLines ||
              hideGridLinesEverySevenLines ||
              ticksConfig?.showLabelAfterEveryNTicks === 2
          })}>
          <LineChart data={data} margin={{ top: 32, left: isTablet ? (isEngagementChart ? -32 : -16) : 0 }}>
            <CartesianGrid stroke={CHART_STROKE_COLOR} />
            <XAxis
              axisLine={false}
              tickLine={false}
              scale={'time'}
              type={'number'}
              dataKey={'timeInMs'}
              domain={chartDomainAxisX}
              tick={props => <ChartHorizontalAxisTick {...props} ticksConfig={ticksConfig} ticksChart={ticksChart} />}
              interval={0}
              width={475}
              stroke={CHART_STROKE_COLOR}
            />
            <YAxis
              domain={chartDomainAxisY}
              ticks={ticksAxisY}
              allowDecimals={false}
              tickLine={false}
              stroke={CHART_STROKE_COLOR}
              tick={props => <ChartVerticalAxisTick shouldNormalize={shouldNormalize} {...props} />}
            />
            <Tooltip
              cursor={true}
              content={props => (
                <LineChartTooltip
                  {...props}
                  chartData={data}
                  isWeekly={ticksConfig?.displayWeekly}
                  payload={props.payload}
                  socialProfiles={socialProfiles}
                  activeId={activeProfileId}
                  displayMonthly={ticksConfig?.displayMonthly}
                  normalizedChart={shouldNormalize}
                  isEngagementChart={isEngagementChart}
                  hasPrefill={hasPreFillingData}
                  hasPostFill={hasPostFillingData}
                />
              )}
            />
            <Legend content={<ChartLegendSocialProfiles socialProfiles={socialProfiles} view={view} />} />
            {socialProfiles.map(({ id, __typename }) => (
              <React.Fragment key={id}>
                <Line
                  key={id}
                  activeDot={{ onMouseLeave, onMouseEnter: () => onMouseEnter(id) }}
                  dot={false}
                  type={'linear'}
                  dataKey={`${id}.fakeValue`}
                  stroke={COLOR_SOCIAL_PROFILES[__typename ?? 'SocialProfile']}
                  strokeWidth={3}
                  onMouseEnter={() => onMouseEnter(id)}
                  onMouseLeave={onMouseLeave}
                  opacity={activeProfileId && activeProfileId !== id ? 0.5 : 1}
                />
                <Line
                  key={`${STRING_DASHED_PROFILE}${id}`}
                  dot={false}
                  activeDot={false}
                  strokeDasharray={'5 5'}
                  type={'linear'}
                  dataKey={`${STRING_DASHED_PROFILE}${id}.value`}
                  stroke={COLOR_SOCIAL_PROFILES[__typename ?? 'SocialProfile']}
                  strokeWidth={3}
                  onMouseEnter={() => onMouseEnter(id)}
                  onMouseLeave={onMouseLeave}
                  opacity={activeProfileId && activeProfileId !== id ? 0.25 : 0.5}
                />
                <Line
                  key={`${STRING_FAKE_VALUE_DASHED}-${id}`}
                  activeDot={{ onMouseLeave, onMouseEnter: () => onMouseEnter(id) }}
                  dot={false}
                  type={'linear'}
                  dataKey={`${id}.fakeValueDashedLine`}
                  stroke={COLOR_SOCIAL_PROFILES[__typename ?? 'SocialProfile']}
                  strokeWidth={3}
                  onMouseEnter={() => onMouseEnter(id)}
                  onMouseLeave={onMouseLeave}
                  opacity={activeProfileId && activeProfileId !== id ? 0.5 : 1}
                />
                <Line
                  key={`${STRING_FAKE_VALUE_DASHED}-${STRING_DASHED_PROFILE}-${id}`}
                  dot={false}
                  activeDot={false}
                  strokeDasharray={'5 5'}
                  type={'linear'}
                  dataKey={`${STRING_DASHED_PROFILE}${id}.${STRING_FAKE_VALUE_DASHED}`}
                  stroke={COLOR_SOCIAL_PROFILES[__typename ?? 'SocialProfile']}
                  strokeWidth={3}
                  onMouseEnter={() => onMouseEnter(id)}
                  onMouseLeave={onMouseLeave}
                  opacity={activeProfileId && activeProfileId !== id ? 0.25 : 0.5}
                />
              </React.Fragment>
            ))}
            {hasFillingData && (
              <>
                {hasPreFillingData && (
                  <ReferenceArea
                    x1={referenceAreaPreFillingCoordinates?.x1}
                    x2={referenceAreaPreFillingCoordinates?.x2}
                    y1={referenceAreaPreFillingCoordinates?.y1}
                    y2={referenceAreaPreFillingCoordinates?.y2}
                    stroke={FILLING_DATA_FILL_STROKE_COLOR}
                    fill={FILLING_DATA_FILL_STROKE_COLOR}
                    strokeOpacity={0.5}
                  />
                )}
                {hasPostFillingData && (
                  <ReferenceArea
                    x1={referenceAreaPostFillingCoordinates?.x1}
                    x2={referenceAreaPostFillingCoordinates?.x2}
                    y1={referenceAreaPostFillingCoordinates?.y1}
                    y2={referenceAreaPostFillingCoordinates?.y2}
                    stroke={FILLING_DATA_FILL_STROKE_COLOR}
                    fill={FILLING_DATA_FILL_STROKE_COLOR}
                    strokeOpacity={0.5}
                  />
                )}
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartPanel>
  );
};
